import React, { useState, useEffect, useCallback } from 'react';
import { Search, Trash2, Copy, Loader2, Image, Filter, CheckCircle, HardDrive } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImageUrl } from '../lib/imageUrl';

const API_URL = '';

const FILTER_TYPES = [
  { id: '', label: 'Tous' },
  { id: 'products', label: 'Produits' },
  { id: 'categories', label: 'Catégories' },
  { id: 'banners', label: 'Bannières' },
  { id: 'logo', label: 'Logo' },
];

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function AdminMediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStorage, setTotalStorage] = useState(0);
  const [total, setTotal] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [copied, setCopied] = useState(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '24' });
      if (filterType) params.append('upload_type', filterType);
      if (search) params.append('search', search);

      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/media?${params}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setFiles(data.files || []);
        setTotalPages(data.pages || 1);
        setTotalStorage(data.total_storage_bytes || 0);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filterType, search]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleDelete = async (fileId) => {
    if (!window.confirm('Supprimer cette image ? Cette action est irréversible.')) return;
    setDeleting(fileId);
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/media/${fileId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setFiles(prev => prev.filter(f => f.file_id !== fileId));
        setTotal(prev => prev - 1);
        toast.success('Image supprimée');
      } else {
        toast.error('Erreur de suppression');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setDeleting(null);
    }
  };

  const copyUrl = (file) => {
    const fullUrl = resolveImageUrl(file.url);
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(file.file_id);
      toast.success('URL copiée');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div data-testid="admin-media-library">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif text-3xl">Bibliothèque Médias</h1>
        <div className="flex items-center gap-2 text-sm text-[#6B6A68]" data-testid="storage-info">
          <HardDrive size={16} />
          <span>{formatBytes(totalStorage)} utilisés — {total} fichier{total !== 1 ? 's' : ''}</span>
        </div>
      </div>
      <p className="text-sm text-[#6B6A68] mb-6">Toutes les images uploadées sur le site</p>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-1 flex-wrap">
          {FILTER_TYPES.map(ft => (
            <button
              key={ft.id}
              onClick={() => { setFilterType(ft.id); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border ${
                filterType === ft.id
                  ? 'bg-[#1A1514] text-white border-[#1A1514]'
                  : 'bg-white text-[#6B6A68] border-[#E0DFDD] hover:border-[#1A1514]'
              }`}
              data-testid={`filter-${ft.id || 'all'}`}
            >
              {ft.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9C9A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Rechercher par nom..."
            className="w-full pl-9 pr-3 py-2 text-xs border border-[#E0DFDD] focus:outline-none focus:border-[#D4AF37]"
            data-testid="media-search"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-[#D4AF37]" size={28} />
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-16 text-[#6B6A68]">
          <Image size={40} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">Aucune image trouvée</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="media-grid">
          {files.map((file) => (
            <div
              key={file.file_id}
              className="group relative bg-white border border-[#E0DFDD] overflow-hidden"
              data-testid={`media-item-${file.file_id}`}
            >
              <div className="aspect-square bg-[#F5F4F2] overflow-hidden">
                <img
                  src={resolveImageUrl(file.thumbnail_url || file.url)}
                  alt={file.original_filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                  <button
                    onClick={() => copyUrl(file)}
                    className="p-2 bg-white text-[#1A1514] rounded-full hover:bg-[#D4AF37] transition-colors"
                    title="Copier l'URL"
                    data-testid={`copy-url-${file.file_id}`}
                  >
                    {copied === file.file_id ? <CheckCircle size={14} /> : <Copy size={14} />}
                  </button>
                  <button
                    onClick={() => handleDelete(file.file_id)}
                    disabled={deleting === file.file_id}
                    className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50"
                    title="Supprimer"
                    data-testid={`delete-media-${file.file_id}`}
                  >
                    {deleting === file.file_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-2">
                <p className="text-[10px] text-[#1A1514] truncate" title={file.original_filename}>
                  {file.original_filename}
                </p>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[9px] text-[#9E9C9A] uppercase">{file.upload_type}</span>
                  <span className="text-[9px] text-[#9E9C9A]">{formatBytes(file.size)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-8 h-8 text-xs font-medium border transition-colors ${
                page === p ? 'bg-[#1A1514] text-white border-[#1A1514]' : 'bg-white text-[#6B6A68] border-[#E0DFDD] hover:border-[#1A1514]'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
